// Font utility for local fonts
export interface FontFamily {
  name: string;
  value: string;
  weights: number[];
  category: string;
}

// All available fonts organized by category (updated weights)
export const fontFamilies: FontFamily[] = [
  // Sans-serif Fonts
  { name: 'Inter', value: 'Inter', weights: [300, 400, 600, 700], category: 'Sans-serif' },
  { name: 'Open Sans', value: 'Open Sans', weights: [300, 400, 600, 700], category: 'Sans-serif' },
  { name: 'Roboto', value: 'Roboto', weights: [300, 400, 500, 700], category: 'Sans-serif' },
  { name: 'Source Sans Pro', value: 'Source Sans Pro', weights: [300, 400, 600, 700], category: 'Sans-serif' },
  { name: 'Poppins', value: 'Poppins', weights: [100, 200, 300, 400, 500, 600, 700, 800], category: 'Sans-serif' },
  
  // Serif Fonts
  { name: 'Merriweather', value: 'Merriweather', weights: [300, 400, 700], category: 'Serif' },
  
  // Script Fonts
  { name: 'Pacifico', value: 'Pacifico', weights: [400], category: 'Script' },
  { name: 'Satisfy', value: 'Satisfy', weights: [400], category: 'Script' },
  { name: 'Allura', value: 'Allura', weights: [400], category: 'Script' },
  
  // Monospace Fonts
  { name: 'Inconsolata', value: 'Inconsolata', weights: [300, 400, 600, 700], category: 'Monospace' },
  { name: 'Fira Code', value: 'Fira Code', weights: [300, 400, 500, 600, 700], category: 'Monospace' }
];

// Font mapping to handle system fonts and variations (consistent with server)
export const FONT_MAPPING: { [key: string]: string } = {
  'Arial': 'Inter',
  'Helvetica': 'Inter',
  'Times New Roman': 'Merriweather',
  'Georgia': 'Merriweather',
  'Verdana': 'Open Sans',
  'Tahoma': 'Roboto',
  'Trebuchet MS': 'Poppins',
  'Impact': 'Inter',
  'Comic Sans MS': 'Pacifico',
  'Courier New': 'Inconsolata',
  'Inter': 'Inter',
  'Open Sans': 'Open Sans',
  'Roboto': 'Roboto',
  'Source Sans Pro': 'Source Sans Pro',
  'Poppins': 'Poppins',
  'Merriweather': 'Merriweather',
  'Pacifico': 'Pacifico',
  'Satisfy': 'Satisfy',
  'Allura': 'Allura',
  'Inconsolata': 'Inconsolata',
  'Fira Code': 'Fira Code'
};

// Font loading state
let fontsLoaded = false;
let fontLoadingPromise: Promise<void> | null = null;


// Get the correct font family using mapping
export const getFontFamily = (originalFontFamily: string): string => {
  if (!originalFontFamily) return 'Inter';
  
  // First check if it's already a local font
  const localFont = fontFamilies.find(f => f.value === originalFontFamily);
  if (localFont) {
    return originalFontFamily;
  }
  
  // Then check the mapping
  const mappedFont = FONT_MAPPING[originalFontFamily];
  if (mappedFont) {
    return mappedFont;
  }
  
  // Fallback to Inter
  return 'Inter';
};

// Enhanced font loading function with guarantees
export const loadFonts = async (): Promise<void> => {
  if (fontsLoaded) {
    return Promise.resolve();
  }
  
  if (fontLoadingPromise) {
    return fontLoadingPromise;
  }
  
  fontLoadingPromise = (async () => {
    
    if ('fonts' in document) {
      try {
        // Load all available fonts with proper error handling
        const fontPromises = fontFamilies.map(font => 
          font.weights.map(weight => 
            document.fonts.load(`${weight} 16px ${font.value}`).catch(error => {
              console.warn(`Failed to load font ${font.value} weight ${weight}:`, error);
              return null;
            })
          )
        ).flat();
        
        await Promise.all(fontPromises);
        
        // Verify fonts are actually loaded
        const fontCheckPromises = fontFamilies.map(font => 
          document.fonts.check(`16px ${font.value}`)
        );
        
        await Promise.all(fontCheckPromises);
        fontsLoaded = true;
      } catch (error) {
        console.error('Error loading fonts:', error);
        fontsLoaded = true; // Mark as done even on error to prevent re-attempts
      }
    } else {
      fontsLoaded = true;
    }
  })();
  
  return fontLoadingPromise;
};

// Load a specific font with guarantee
export const loadSpecificFont = async (fontFamily: string, weight: number = 400): Promise<boolean> => {
  const targetFont = getFontFamily(fontFamily);
  
  if ('fonts' in document) {
    try {
      await document.fonts.load(`${weight} 16px ${targetFont}`);
      const isLoaded = document.fonts.check(`16px ${targetFont}`);
      return isLoaded;
    } catch (error) {
      console.warn(`Failed to load font ${fontFamily} -> ${targetFont}:`, error);
      return false;
    }
  }
  
  return true; // Assume loaded if Font Loading API not available
};

// Check if fonts are loaded
export const areFontsLoaded = (): boolean => {
  return fontsLoaded;
};

// Get available weights for a specific font
export const getAvailableWeights = (fontFamily: string): number[] => {
  const font = fontFamilies.find(f => f.value === fontFamily);
  return font ? font.weights : [300, 400, 600, 700];
};

// Get font weight name
export const getWeightName = (weight: number): string => {
  switch (weight) {
    case 100: return 'Thin';
    case 200: return 'Extra Light';
    case 300: return 'Light';
    case 400: return 'Regular';
    case 500: return 'Medium';
    case 600: return 'Semi Bold';
    case 700: return 'Bold';
    case 800: return 'Extra Bold';
    default: return 'Regular';
  }
};

// Preload critical fonts
export const preloadCriticalFonts = async (): Promise<void> => {
  const criticalFonts = ['Inter', 'Open Sans', 'Roboto', 'Poppins'];
  
  if ('fonts' in document) {
    const promises = criticalFonts.map(async (font) => {
      try {
        const weights = getAvailableWeights(font);
        await Promise.all(
          weights.map(weight => 
            document.fonts.load(`${weight} 16px ${font}`).catch(() => null)
          )
        );
      } catch (error) {
        console.warn(`Failed to preload ${font}:`, error);
      }
    });
    
    await Promise.all(promises);
  } else {
  }
};

// Test font loading with detailed results
export const testFontLoading = async (): Promise<Array<{ font: string; loaded: boolean }>> => {
  const results = await Promise.all(
    fontFamilies.map(async (font) => {
      if ('fonts' in document) {
        try {
          await document.fonts.load(`400 16px ${font.value}`);
          const isLoaded = document.fonts.check(`16px ${font.value}`);
          return { font: font.value, loaded: isLoaded };
        } catch (error) {
          console.warn(`Failed to load font ${font.value}:`, error);
          return { font: font.value, loaded: false };
        }
      }
      return { font: font.value, loaded: true }; // Assume loaded if API not available
    })
  );
  
  return results;
}; 

// Test function to verify all fonts are loading correctly
export const testAllFonts = async (): Promise<void> => {
  try {
    await loadFonts();
    
    // Test individual font loading
    const results = await testFontLoading();
    
    // Summary
    const loadedCount = results.filter(r => r.loaded).length;
    const totalCount = results.length;
    
    if (loadedCount === totalCount) {
      // All fonts loaded successfully
    } else {
      console.warn('⚠️  Some fonts failed to load. Check the console for details.');
    }
  } catch (error) {
    console.error('❌ Font loading failed:', error);
  }
};

// Function to test specific font weights
export const testFontWeights = async (fontFamily: string): Promise<void> => {
  const weights = getAvailableWeights(fontFamily);
  
  if ('fonts' in document) {
    for (const weight of weights) {
      try {
        await document.fonts.load(`${weight} 16px ${fontFamily}`);
        const isLoaded = document.fonts.check(`16px ${fontFamily}`);
        if (!isLoaded) {
          console.warn(`❌ Failed to load weight ${weight} for ${fontFamily}`);
        }
      } catch (error) {
        console.error(`❌ Failed to load weight ${weight} for ${fontFamily}:`, error);
      }
    }
  }
};

// Function to get font loading status
export const getFontStatus = (): { total: number; loaded: number; fonts: Array<{ name: string; loaded: boolean }> } => {
  const fonts = fontFamilies.map(font => ({
    name: font.value,
    loaded: 'fonts' in document ? document.fonts.check(`16px ${font.value}`) : true
  }));
  
  const loaded = fonts.filter(f => f.loaded).length;
  
  return {
    total: fonts.length,
    loaded,
    fonts
  };
}; 